import PopOver, { IPopOverProps } from "~/ui/popover";
import { node } from "~/util/dom";
import { CONFIG, CONF3D, WASM } from "~/config";
import { store } from "~/state/store";
import { setMapset } from "~/state/map/state";
import { MAP_OVERLAYS } from "~/state/map/constants";
import { _ } from "~/i18n";
import { setMapsetUserSettings, setOverlaysUserSettings } from "~/util/providers/mapybox";
import { getMapsetBySEO } from "~/util/mapset";
import { isPointInStatePolygon } from "~/util/state-polygon";
import Tooltip, { ETooltipFlow } from "~/ui/tooltip";
import { chmapLog } from "~/util/analyticsAction";
import { selectUserSettingsValue } from "~/state/user/selectors";
import { ESETTINGS_KEYS } from "~/state/user/interfaces";
import { IMapState } from "~/state/map/interfaces";

interface IOPTION {
	title: string;
	value: MAP_OVERLAYS;
}

const RETINA_IMAGES = ["dopravni", "letecka", "turisticka", "zakladni", "zimni"];

const getCommonOptions: () => Array<IOPTION> = () => [
	{
		title: _("mapsetMenu.overlay.cadastral"),
		value: MAP_OVERLAYS.CADASTRAL,
	},
	{
		title: _("mapsetMenu.overlay.photo"),
		value: MAP_OVERLAYS.PHOTO,
	},
	{
		title: _("mapsetMenu.overlay.webcam"),
		value: MAP_OVERLAYS.WEBCAM,
	},
];

const getWinterOptions: () => Array<IOPTION> = () => [
	{
		title: _("mapsetMenu.overlay.cadastral"),
		value: MAP_OVERLAYS.CADASTRAL,
	},
	{
		title: _("mapsetMenu.overlay.hillshade"),
		value: MAP_OVERLAYS.HILLSHADE,
	},
	{
		title: _("mapsetMenu.overlay.photo"),
		value: MAP_OVERLAYS.PHOTO,
	},
	{
		title: _("mapsetMenu.overlay.webcam"),
		value: MAP_OVERLAYS.WEBCAM,
	},
];

const OPHOTO_EXCLUSIVE_OPTIONS: Array<IOPTION> = [{
	title: _("mapsetMenu.overlay.turist"),
	value: MAP_OVERLAYS.TURIST,
}, {
	title: _("mapsetMenu.overlay.traffic"),
	value: MAP_OVERLAYS.TRAFFIC,
}];

const WINTER_EXCLUSIVE_OPTIONS: Array<IOPTION> = [{
	title: _("mapsetMenu.overlay.ski"),
	value: MAP_OVERLAYS.SKI,
}];

/** pocet viditelnych mapsetu na prvni otevreni menu */
const VISIBLE_MAPSETS = 5;

function setToggle(toggle: HTMLMapyToggleElement, value: MAP_OVERLAYS, mapset: string, exclusive = false) {
	if (toggle.checked && exclusive) {
		Array.from(toggle.closest("mapy-mapmenu-mapset-options").querySelectorAll("mapy-toggle")).forEach(et => {
			if (et !== toggle) {
				et.checked = false;
			}
		});
	}

	const overlays = Array.from(toggle.closest("mapy-mapmenu-mapset").querySelectorAll("mapy-toggle")).filter(tg => tg.checked).map(tg => parseInt(tg.dataset.value));
	const overlaysToSave = [...overlays];
	const state = store.getState();
	const { user } = state.user;

	// pridame panorama, pokud je aktivni
	if (state.map.overlays.indexOf(MAP_OVERLAYS.PANORAMA) !== -1) {
		overlays.push(MAP_OVERLAYS.PANORAMA);
	}

	// analytika
	chmapLog({
		mapsetNew: mapset,
		overlays: state.map.mapset in state.map.currentOverlays ? [...state.map.currentOverlays[state.map.mapset]] : [],
		overlaysNew: state.map.provider === "map" ? overlays : [],
		...toggle.checked ? { overlayAdd: value } : { overlayRemove: value },
	});

	store.dispatch(setMapset({
		mapset,
		overlays,
	}));
	Mapy.getComponent("history").save();

	if (state.map.provider === "map" && user.state === "green") {
		const defaultMapset = selectUserSettingsValue(state, ESETTINGS_KEYS.MAPSET_WEB_DEFAULT_ID);

		// pokud chceme vzdy posledni mapu, tak s kazdou zmenou ukladame overlaye, protoze patri k mape, co prave vidime - ukladame i s mapsetem, protoze se mohlo stat, ze jsme nejak nemeli ulozeny ten spravny
		if (defaultMapset === 0) {
			setMapsetUserSettings(getMapsetBySEO(mapset)?.id, overlaysToSave);
		// pokud chceme konkretni mapset, tak ukladame overlaye jen v pripade, ze mame zrovna zapnuty ten zvoleny
		} else if (defaultMapset === getMapsetBySEO(mapset)?.id) {
			setOverlaysUserSettings(overlaysToSave);
		}
	}
}

function getProviderMapsets(mapState: IMapState) {
	if (mapState.provider === "m3d") {
		return CONF3D;
	} else if (mapState.provider === "wasm") {
		return WASM;
	}

	return CONFIG.mapsets;
}

interface IMapsetMenuProps {
	maxVisibleMapsets?: number;
}

export default class MapsetMenu {
	#popOver: PopOver;

	#content: HTMLMapyMapmenuElement;

	#cadastralTooltips: Array<Tooltip>;

	#maxVisibleMapsets: number;

	constructor(props: Pick<IPopOverProps, "anchor" | "onClose" | "onOpen" | "position" | "flow" | "activator" | "clickOutClose"> & IMapsetMenuProps) {
		this.#content = node("mapy-mapmenu", { className: "mapsetMenu__mapy-mapmenu" });
		const { anchor, onClose, onOpen, position, flow, activator, clickOutClose, maxVisibleMapsets } = props;

		this.#maxVisibleMapsets = maxVisibleMapsets || VISIBLE_MAPSETS;
		this.#popOver = new PopOver({
			content: this.#content,
			anchor,
			onClose,
			onOpen,
			offset: [8, 8, 8, 8],
			position,
			flow,
			activator,
			className: "mapsetMenu__popOver",
			clickOutClose,
		});
		this.#cadastralTooltips = [];
	}

	get element() {
		return this.#content;
	}

	destroy() {
		this.#popOver.destroy();
		this.#popOver = null;
	}

	async open() {
		await this.#build(this.#content);
		this.#popOver.open();
	}

	close() {
		this.#popOver.close();
		this.#cadastralTooltips.forEach(tooltip => {
			tooltip.hide();
			tooltip.destroy();
		});
		this.#cadastralTooltips = [];
	}

	async #build(content: HTMLMapyMapmenuElement) {
		const state = store.getState();
		const inCZPolygon = await isPointInStatePolygon(state.map.longitude, state.map.latitude, {
			cs: true,
		});
		const providerMapsets = getProviderMapsets(state.map);
		const mapsets = providerMapsets.filter(mapset => !mapset.hidden && ["katastralni", "fotografie"].indexOf(mapset.seo) === -1 && ((inCZPolygon && CONFIG.defaultLang === "cs") || ["zemepisna", "19stoleti", "atlas"].indexOf(mapset.seo) === -1));

		// vybuildime polozky
		mapsets.forEach((mapset, mapsetIndex) => {
			const item = node("mapy-mapmenu-mapset");
			const options = node("mapy-mapmenu-mapset-options");

			if (state.map.mapset === mapset.seo) {
				item.active = true;
			}

			const previewEl = node("mapy-mapmenu-mapset-preview");
			const mapsetImg = new Image();
			const img1x = `${mapset.icon_url || `/img/mapset/${mapset.icon || "zakladni"}-small.png?v=${CONFIG.filesVersion}`}`;

			mapsetImg.src = img1x;

			if (RETINA_IMAGES.includes(mapset.icon)) {
				const img2x = `/img/mapset/${mapset.icon}-large.png?v=${CONFIG.filesVersion}`;

				mapsetImg.srcset = `${img1x}, ${img2x} 2x`;
			}

			let top = node("mapy-mapmenu-mapset-main");

			previewEl.append(mapsetImg);
			top.append(
				previewEl,
				document.createTextNode(mapset.name),
			);

			// extra prepinace pro leteckou a zimni
			if (state.map.provider === "map" && (mapset.seo === "letecka" || mapset.seo === "zimni")) {
				const subOptions = node("mapy-mapmenu-mapset-options", { className: "mapsetMenu__subOptions" });

				subOptions.append(
					top,
					...(mapset.seo === "letecka" ? OPHOTO_EXCLUSIVE_OPTIONS : WINTER_EXCLUSIVE_OPTIONS).map(option => {
						const opt = node("mapy-mapmenu-mapset-option", { innerText: option.title });
						const toggle = node("mapy-toggle");

						if (mapset.seo in state.map.currentOverlays) {
							toggle.checked = state.map.currentOverlays[mapset.seo].indexOf(option.value) !== -1;
						} else if (mapset.seo === "zimni" && option.value === MAP_OVERLAYS.SKI) {
							toggle.checked = true;
						}

						toggle.dataset.value = option.value.toString();
						opt.addEventListener("click", (event: Event) => {
							if (event.type === "click" && event.target !== opt) {
								return;
							}

							toggle.checked = !toggle.checked;
							setToggle(toggle, option.value, mapset.seo, true);
						}, false);
						toggle.addEventListener("change", () => {
							setToggle(toggle, option.value, mapset.seo, true);
						}, false);
						opt.append(toggle);

						return opt;
					}),
				);

				top = subOptions;
			}

			options.append(
				top,
				...state.map.provider !== "map" || ["zemepisna", "19stoleti", "atlas", "hapticka"].indexOf(mapset.seo) !== -1
					? []
					: (mapset.seo === "zimni" ? getWinterOptions() : getCommonOptions()).filter(option => (["cs"].indexOf(CONFIG.defaultLang) !== -1 || inCZPolygon) || [MAP_OVERLAYS.CADASTRAL].indexOf(option.value) === -1).map(option => {
						const opt = node("mapy-mapmenu-mapset-option", { innerText: option.title });
						const toggle = node("mapy-toggle");

						if (mapset.seo in state.map.currentOverlays) {
							toggle.checked = state.map.currentOverlays[mapset.seo].indexOf(option.value) !== -1;
						}

						toggle.dataset.value = option.value.toString();

						// v zahranici zakazeme katastr, ale porad ho ukazujeme
						if (!inCZPolygon && [MAP_OVERLAYS.CADASTRAL].indexOf(option.value) !== -1) {
							const span = node("span");

							span.append(toggle);
							opt.append(span);

							toggle.disabled = true;
							opt.classList.add("mapsetMenu__mapy-mapmenu-mapset--disabled");

							this.#cadastralTooltips.push(new Tooltip({
								anchor: toggle,
								trigger: span,
								content: node("p", {}, _(`mapsetSwitch.cadastralDisabled`)),
								hideTimeout: 0,
								width: 200,
								flow: ETooltipFlow.END,
								margin: [0, -6, 0, 0],
							}));
						} else {
							opt.append(toggle);
							opt.addEventListener("click", (event: Event) => {
								if (event.type === "click" && event.target !== opt) {
									return;
								}

								toggle.checked = !toggle.checked;
								setToggle(toggle, option.value, mapset.seo);
							}, false);
							toggle.addEventListener("change", () => {
								setToggle(toggle, option.value, mapset.seo);
							}, false);
						}

						return opt;
					}),
			);
			item.addEventListener("click", () => {
				if (item.active) {
					return;
				}

				if (mapset.seo === "atlas" || mapset.seo === "hapticka") {
					this.close();

					return;
				}

				const active = content.querySelector("mapy-mapmenu-mapset[active]") as HTMLMapyMapmenuMapsetElement;
				const overlays = Array.from(item.querySelectorAll("mapy-toggle")).filter(toggle => toggle.checked).map(toggle => parseInt(toggle.dataset.value));
				const overlaysToSave = [...overlays];
				const mapState = store.getState().map;

				// pridame panorama, pokud je aktivni
				if (state.map.overlays.indexOf(MAP_OVERLAYS.PANORAMA) !== -1) {
					overlays.push(MAP_OVERLAYS.PANORAMA);
				}

				if (active) {
					active.active = false;
				}
				item.active = true;

				// analytika
				chmapLog({
					mapsetNew: `${mapset.seo}${state.map.provider === "m3d" ? "_3D" : ""}`,
					overlays: state.map.provider === "m3d" ? [] : mapState.mapset in mapState.currentOverlays ? [...mapState.currentOverlays[mapState.mapset]] : [],
					overlaysNew: state.map.provider === "m3d" ? [] : overlays,
				});

				store.dispatch(setMapset({
					mapset: mapset.seo,
					...state.map.provider === "map" ? { overlays } : {},
				}));
				Mapy.getComponent("history").save();

				const { user } = state.user;

				if (state.map.provider === "map" && user.state === "green") {
					const defaultMapset = selectUserSettingsValue(state, ESETTINGS_KEYS.MAPSET_WEB_DEFAULT_ID);

					// pokud chceme vzdycky posledni mapu, ukladame si i jeji overlaye
					if (defaultMapset === 0) {
						setMapsetUserSettings(getMapsetBySEO(mapset.seo)?.id, overlaysToSave);
					// pokud chceme nejakou konkretni, tak se overlaye ukladani jinde a to jen, kdyz je ta zvolena mapa aktivni
					} else {
						setMapsetUserSettings(getMapsetBySEO(mapset.seo)?.id);
					}
				}
			});
			item.append(options);

			// hapticka a atlas jsou odkazy
			if (mapset.seo === "atlas" || mapset.seo === "hapticka") {
				const link = node("a");

				if (mapset.seo === "atlas") {
					link.href = `https://atlas.mapy.${CONFIG.TLD}/`;
					link.target = "_blank";
				} else {
					const { x, y, z } = state.map;

					link.href = `https://hapticke.mapy.${CONFIG.TLD}?x=${x}&y=${y}&z=${z}&lang=${CONFIG.defaultLang}`;
				}

				item.tabIndex = -1;
				link.append(item);
				content.append(link);

				if (mapsetIndex >= this.#maxVisibleMapsets) {
					link.classList.add("mapy-mapmenu-mapset--hidden");
				}
			} else {
				if (mapsetIndex >= this.#maxVisibleMapsets && !item.active) {
					item.classList.add("mapy-mapmenu-mapset--hidden");
				}
				content.append(item);
			}
		});

		// tlacitko pro zobrazeni dalsich mapsetu
		if (mapsets.length > this.#maxVisibleMapsets) {
			const moreButton = node("button", { type: "button", innerText: _("mapsetMenu.otherMaps"), className: "mapy-mapmenu-mapset__otherMaps" });

			moreButton.addEventListener("click", () => {
				const hiddenMapsets = Array.from(content.querySelectorAll(".mapy-mapmenu-mapset--hidden"));

				hiddenMapsets.forEach(mapset => mapset.classList.remove("mapy-mapmenu-mapset--hidden"));
				moreButton.remove();
			}, false);
			content.appendChild(moreButton);
		}
	}
}
